import type { RecordFormat, RecordField } from '@adhd/shared';
import type { SheetRecord } from './RecordSheet.js';

/**
 * The landscape worksheet, laid out the way the paper one is.
 *
 * Modelled on the arrangement of the printed CBT thought record rather than on
 * a plain table, because the arrangement is the part people recognise and the
 * part that teaches the method:
 *
 *   - three narrow columns on the left, in the order the work happens:
 *     what happened, what you felt, what went through your mind
 *   - evidence for and evidence against STACKED in the middle, not side by
 *     side, which is what gives them the width to hold a sentence
 *   - the response as a full-height column on the right, because it is written
 *     against everything to its left
 *
 * And the rows are numbered. One sheet carries three separate situations, and
 * the number is what ties row 2 of the response to row 2 of the situation. That
 * is why this takes an array: three records to a sheet, not one.
 *
 * The wording throughout is this app's own — the labels and prompts already
 * written in record-formats.ts. The published worksheet this borrows its shape
 * from is somebody's copyrighted work, and copying its sentences into a
 * codebase is a different thing from following the clinical convention its
 * columns describe.
 */
interface Props {
  records: readonly SheetRecord[];
  format: RecordFormat;
  /** Sheet n of m, when a range spills over more than one. */
  sheetNumber?: number;
  sheetCount?: number;
}

/**
 * One situation per sheet.
 *
 * This said 3 while the component below rendered `records[0]` and nothing else,
 * so every sheet silently dropped two of the three records its caller had
 * sliced into it. Six records printed as two, under a page header that still
 * counted six — on "the worksheet grid", the layout the Export screen calls
 * "what a therapist is used to reading".
 *
 * The component was deliberately changed to one-per-sheet: three-to-a-sheet was
 * what forced a circled number onto every cell to say which record a box
 * belonged to. The constant was simply never brought along, and because the
 * arithmetic still divided cleanly nothing looked wrong until somebody counted
 * the situations on the printed page.
 */
export const ROWS_PER_SHEET = 1;

export function WorksheetSheet({ records, format, sheetNumber, sheetCount }: Props): JSX.Element {
  // One record per sheet. Three-to-a-sheet was what forced a circled number
  // onto every cell to say which record a box belonged to, and blanks — the
  // only case where three situations genuinely share a page — now have their
  // own portrait component.
  const rows = [records[0] ?? null];
  const has = (field: RecordField): boolean => format.steps.some((s) => s.field === field);

  return (
    <article className="ws">
      <header className="ws-head">
        {/* Keyed off the format, not its display name — comparing against the
            literal string meant renaming the format in record-formats.ts would
            silently retitle every printed sheet. */}
        <h2>{format.key === 'standard' ? 'Thought record' : format.name}</h2>
        <span className="ws-head-note">
          {/* The date, because this is loose paper.
              Five sheets left a desk with nothing on any of them saying which
              record it was or when — the only date was on the page header,
              which is one sheet out of five. Separate them and they become
              anonymous. The broadsheet has carried a date and a foot rule since
              it was written; this layout never got either. */}
          {records[0] ? `${formatSheetDay(records[0].occurredAt)} · ` : ''}
          {format.when}
          {sheetNumber && sheetCount && sheetCount > 1 && (
            <> · sheet {sheetNumber} of {sheetCount}</>
          )}
        </span>
      </header>

      <div className="ws-grid">
        {has('situation') && (
          <Column
            title={format.labels.situation}
            ask={stepHint(format, 'situation')}
            rows={rows}
            // Per row, not per sheet: one sheet holds three situations and they
            // are routinely filled in days apart.
            render={(r) => r.situation.trim()}
          />
        )}

        {has('emotions') && (
          <Column
            title="What you felt"
            ask={stepHint(format, 'emotions')}
            rows={rows}
            render={(r) =>
              r.emotions.length === 0
                ? ''
                : r.emotions
                    .map(
                      (e) =>
                        `${e.emotion} — ${e.intensityBefore}%` +
                        (e.intensityAfter !== null && e.intensityAfter !== e.intensityBefore
                          ? ` → ${e.intensityAfter}%`
                          : ''),
                    )
                    .join('\n')
            }
          />
        )}

        {has('thought') && (
          <Column
            title={format.labels.thought}
            ask={stepHint(format, 'thought')}
            rows={rows}
            render={(r) => {
              const thought = r.automaticThought.trim();
              if (!thought) return '';
              return r.beliefBefore !== null
                ? `${thought}\n\n${format.beliefLabel} ${r.beliefBefore}%`
                : thought;
            }}
          />
        )}

        {/* The middle block. Stacked rather than two more columns, which is what
            lets a piece of evidence be a sentence instead of three words. */}
        {(has('evidenceFor') || has('evidenceAgainst')) && (
          <div className="ws-mid">
            {has('evidenceFor') && (
              <Column
                title={format.labels.evidenceFor}
                ask={stepHint(format, 'evidenceFor')}
                rows={rows}
                render={(r) => evidenceText(r, 'for')}
              />
            )}
            {has('evidenceAgainst') && (
              <Column
                title={format.labels.evidenceAgainst}
                ask={stepHint(format, 'evidenceAgainst')}
                rows={rows}
                render={(r) => evidenceText(r, 'against')}
              />
            )}
          </div>
        )}

        {has('response') && (
          <Column
            title={format.labels.response}
            ask={stepHint(format, 'response')}
            rows={rows}
            render={(r) => {
              const response = (r.response ?? '').trim();
              if (response) {
                return r.beliefAfter !== null
                  ? `${response}\n\nBelieved afterwards: ${r.beliefAfter}%`
                  : response;
              }
              // Named rather than left blank. An unfinished record is a thought
              // written down and never answered, and that is the thing worth
              // talking about — a silently empty box reads as a printing fault.
              return '— not finished —';
            }}
          />
        )}
      </div>
      {/* The same line the broadsheet has carried since it was written. Paper
          that leaves a building should say what it is and that it is private. */}
      <div className="ws-foot">
        <span>ADHD &amp; Me · {format.key === 'standard' ? 'Thought record' : format.name} · Private</span>
      </div>
    </article>
  );
}

function Column({
  title,
  ask,
  rows,
  render,
}: {
  title: string;
  ask: string | null;
  rows: readonly (SheetRecord | null)[];
  render: (record: SheetRecord) => string;
}): JSX.Element {
  return (
    <section className="ws-col">
      <header className="ws-col-head">
        <h3>{title}</h3>
        {ask && <p>{ask}</p>}
      </header>
      <div className="ws-col-body">
        {rows.map((record, i) => (
          <div className="ws-cell" key={i}>
            <div className="ws-cell-text">{record ? render(record) : null}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function evidenceText(record: SheetRecord, side: 'for' | 'against'): string {
  return record.evidence
    .filter((e) => e.side === side)
    .map((e) => e.text)
    .join('\n');
}

/**
 * The question under a column heading, taken from the format's own step.
 *
 * Falls back to the step's label, because the emotion step carries no hint —
 * on screen its meaning comes from the picker underneath it, and on paper that
 * left the column with a heading and nothing telling you what to write.
 */
function stepHint(format: RecordFormat, field: RecordField): string | null {
  const step = format.steps.find((s) => s.field === field);
  return step?.hint ?? step?.label ?? null;
}

/**
 * Short and unambiguous on paper: no weekday, no ordinal, year included.
 *
 * From `occurredAt`, which is the instant. The broadsheet formats the same
 * field, so the two layouts date a record the same way.
 */
function formatSheetDay(occurredAt: string): string {
  const d = new Date(occurredAt);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
